import { Autocomplete as AutocompleteField } from "@components/common/autocomplete/autocomplete";
import { Form } from "@components/common/form/form/form";
import { InputTextField } from "@components/common/form/input-text/input-text-field";
import debounce from "lodash/debounce";
import { FunctionComponent, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { completeSearch } from "@helper/search/search-api-helper";
import { Option, type OptionProps } from "@components/common/option/option";
import { useRouter } from "next/router";
import SearchStore from "@helper/search/search-store";
import { Chip } from "@components/common/chip/chip";
import { History } from "@components/common/icons/history";
import { Button } from "@components/common/button/button";
import { useTranslation } from "react-i18next";
import { Spinner } from "@components/common/spinner/spinner";
import { Search } from "@components/common/icons/search";
import { OptionGroup } from "@components/common/option/option-group/option-group";
import styles from "@components/search/autocomplete/autocomplete.module.scss";

type Suggestion = {
    name: string;
    username: string;
    type: string;
};

export const Autocomplete: FunctionComponent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const history = localStorage.getItem("searchHistory");
    const setSearchValue = SearchStore((state) => state.setSearchValue);
    const router = useRouter();
    const { t } = useTranslation();
    const [storage, setStorage] = useState<string[]>(history ? JSON.parse(history) : []);
    const { control, watch, setValue, handleSubmit } = useForm({
        defaultValues: { query: "" },
    });
    const value = ((watch("query") as any) || "").toString();
    const debouncedSearch = useRef(
        debounce(async (value) => {
            setLoading(true);
            try {
                const result = await completeSearch(value);
                setSuggestions(result);
                setLoading(false);
            } catch (e) {
                console.error(e);
            }
        }, 300),
    ).current;

    const goToSearch = (value: string, username?: string, type?: string) => {
        const newElement = username ? JSON.stringify({ name: value, username, type }) : value;
        const newLocalStorage = [newElement, ...storage.filter((e) => e !== newElement)];
        localStorage.setItem("searchHistory", JSON.stringify(newLocalStorage));
        setStorage(newLocalStorage);
        setOpen(false);
        if (username) {
            return router.push(`/profile/${type === "user" ? "info" : "company"}/${username}`);
        }
        if (value && value.trim() !== "") {
            setSearchValue(value);
            router.push(`/search?query=${encodeURIComponent(value)}`);
        }
    };

    const removeHistory = (value: string) => {
        const temporary = storage.filter((e) => e !== value);
        localStorage.setItem("searchHistory", JSON.stringify(temporary));
        setStorage(temporary);
    };

    const formatSuggestionStart = (searchString: string, wholeString: string): string => {
        if (wholeString.toLowerCase().startsWith(searchString.toLowerCase())) {
            return searchString;
        }
        return "";
    };

    const formatSuggestionEnd = (searchString: string, wholeString: string): string => {
        if (wholeString.toLowerCase().startsWith(searchString.toLowerCase())) {
            return wholeString.slice(searchString.length);
        }
        return wholeString;
    };

    const handleClickEnter = (element: Element) => {
        const el = element as HTMLElement;
        if (typeof el.click === "function") {
            el.click();
        }
    };

    const recentlySearched: OptionProps[] = storage
        .slice(0, 6)
        .map((value: string, index) => {
            if (value?.includes("username")) {
                const suggestion = JSON.parse(value);
                const { username, name, type } = suggestion;
                if (!name) return null;
                return {
                    label: (
                        <div className="flex items-center gap-x-2">
                            <div>{name}</div>
                            <div>
                                <Chip
                                    size="xs"
                                    rounded="md"
                                    label={type === "user" ? "Realclixx user" : "Realclixx company"}
                                    color={type === "user" ? "primary" : "orange"}
                                />
                            </div>
                        </div>
                    ),
                    onClick: () => goToSearch(name, username, type),
                    leftAdornments: <History />,
                    rightAdornments: (
                        <Button
                            label={t("search.removeHistoryItem", { defaultValue: "remove" })}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                removeHistory(value);
                            }}
                            size="xs"
                            color="primary"
                            text={true}
                        />
                    ),
                };
            }
            if (!value) return null;
            return {
                label: value,
                onClick: () => goToSearch(value),
                leftAdornments: <History />,
                rightAdornments: (
                    <Button
                        label={t("search.removeHistoryItem", { defaultValue: "remove" })}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            removeHistory(value);
                        }}
                        size="xs"
                        color="primary"
                        text={true}
                    />
                ),
            };
        })
        .filter(Boolean);

    const withoutProfileOptions: OptionProps[] = (suggestions?.filter((s) => !s.type) ?? [])
        .map((suggestion) => {
            const { name } = suggestion;
            if (!name) return null;
            return {
                label: (
                    <span>
                        {formatSuggestionStart(value, name)}
                        <span className="text-primary-500">{formatSuggestionEnd(value, name)}</span>
                    </span>
                ),
                onClick: () => goToSearch(name),
            };
        })
        .filter(Boolean)
        .slice(0, 8) as OptionProps[];

    const profileOptions: OptionProps[] = (suggestions?.filter((s) => s.type === "user" || s.type === "company") ?? [])
        .map((suggestion) => {
            const { name, username, type } = suggestion;
            if (!name) return null;
            return {
                label: (
                    <div className="flex items-center gap-x-2">
                        <div>
                            <span>
                                {formatSuggestionStart(value, name)}
                                <span className="text-primary-500">{formatSuggestionEnd(value, name)}</span>
                            </span>
                        </div>
                        <div>
                            <Chip
                                size="xs"
                                rounded="md"
                                label={type === "user" ? "Realclixx user" : "Realclixx company"}
                                color={type === "user" ? "primary" : "orange"}
                            />
                        </div>
                    </div>
                ),
                onClick: () => goToSearch(name, username, type),
            };
        })
        .filter(Boolean)
        .slice(0, 8) as OptionProps[];

    return (
        <Form onSubmit={handleSubmit(() => goToSearch(value))}>
            <AutocompleteField
                input={
                    <InputTextField
                        name="query"
                        control={control}
                        onChange={(event) => {
                            const value = event.target.value;
                            debouncedSearch(value);
                            setOpen(true);
                            setValue("query", value);
                        }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setOpen(false)}
                        rounded="full"
                        color="white"
                        block={true}
                        rightIndicator={
                            <div className="relative size-6 translate-x-2">
                                {loading ? (
                                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                        <Spinner color="primary" />
                                    </div>
                                ) : (
                                    <Button
                                        leftAdornments={<Search />}
                                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                                        rounded="full"
                                        size="sm"
                                        type="submit"
                                        color="primary"
                                    />
                                )}
                            </div>
                        }
                        shadow={true}
                        placeholder={t("search.placeholder", { defaultValue: "Search ..." })}
                        className="dark:rounded-full dark:border-2 dark:border-gray-500"
                    />
                }
                open={open}
                focusMode="hover"
                fitWidth={true}
                hoverClass="bg-gray-100"
                onClickEnter={handleClickEnter}
            >
                <div className="py-1.5">
                    {value.length > 0 ? (
                        <>
                            {withoutProfileOptions.length > 0 || profileOptions.length > 0 ? (
                                <>
                                    {withoutProfileOptions.length > 0 && withoutProfileOptions.map((item, index) => <Option {...item} key={index} />)}
                                    {profileOptions.length > 0 && (
                                        <OptionGroup label={t("search.realclixxResults", { defaultValue: "Realclixx Results" })}>
                                            {profileOptions.map((item, index) => (
                                                <Option {...item} key={index} />
                                            ))}
                                        </OptionGroup>
                                    )}
                                </>
                            ) : (
                                <div className={styles.NoResults}>
                                    {t("search.noResultsFound", { defaultValue: "No entries could be found for your search" })}
                                </div>
                            )}
                        </>
                    ) : recentlySearched.length > 0 ? (
                        <OptionGroup label={t("search.recentlySearched", { defaultValue: "Recently searched" })}>
                            {recentlySearched.map((item, index) => (
                                <Option {...item} key={index} />
                            ))}
                        </OptionGroup>
                    ) : (
                        <div className={styles.Empty}>{t("search.enterSearchTerm", { defaultValue: "Enter a search term" })}</div>
                    )}
                </div>
            </AutocompleteField>
        </Form>
    );
};
